<template>
  <div>
    <!-- Search form -->
    <div class="d-flex flex-wrap mb-4">
      <SearchInput
        ref="search-input-component"
        class="flex-grow-1"
        v-model="searchQuery"
        :placeholder="gettext('Search stores...')"
        @input="
          fetchStoreviewset({
            page: 1,
            query: searchQuery,
            country: selectedCountry.id,
          })
        "
        @reset="
          searchQuery = '';
          fetchStoreviewset({
            page: 1,
            query: '',
            country: selectedCountry.id,
          });
        "
      />
      <!-- Country selection -->
      <FilterDropdown
        :label="gettext('Sustainability label')"
        :options="filteredAndOrderedCountries"
        class="country-selection mt-2 mt-sm-0 ms-sm-2"
        @select="onCountrySelect($event)"
      >
        <template #label>
          <span class="button-label">
            {{ gettext('Delivers to') }}
            <img
              :src="`/static/img/icons/countries/${selectedCountry.country_code}.png`"
            />
            <IconChevronDown />
          </span>
        </template>
        <template #option="{ option }">
          <img
            class=""
            :src="`/static/img/icons/countries/${option.country_code}.png`"
          />
          {{ option.name }}
        </template>
      </FilterDropdown>
    </div>

    <!-- Tiles -->
    <div class="tiles mb-4">
      <div v-if="isLoading" class="my-4 mx-auto"><IconLoading /></div>
      <article
        v-for="store in $store.state.viewset.results"
        v-else-if="$store.state.viewset.results.length > 0"
        :key="store.id"
        class="brand-store-tile"
        @click.prevent="
          storeModalStore = store;
          showStoreModal = true;
        "
      >
        <div class="logo">
          <a :href="store.get_absolute_url" @click.prevent>
            <img
              :src="store.logo"
              :alt="gettext('Logo of') + ' ' + store.store_name"
            />
          </a>
        </div>
      </article>
      <p v-else class="lead py-4 mx-auto">
        {{
          gettext('Unfortunately there are no results for the current search')
        }}
      </p>
    </div>

    <!-- Pagination -->
    <Pagination
      :url="viewURL"
      page-query-param="page"
      :page="currentPageNumber"
      :show-extra="2"
      :start="1"
      :end="Math.ceil($store.state.viewset.count / limit)"
    />

    <!-- Modal -->
    <Modal v-model="showStoreModal" size="modal-lg" :scrollable="true">
      <div v-if="storeModalStore">
        <!-- Title -->
        <h3 class="mb-4">{{ storeModalStore.store_name }}</h3>

        <!-- Brand info -->
        <h4 class="h5" v-if="storeModalStore.is_brand">
          {{ gettext('About the brand') }}
        </h4>
        <div
          class="admin-text admin-text-no-empty-paragraphs mb-4"
          v-if="storeModalStore.is_brand"
          v-html="storeModalStore.is_brand.about_brand"
        ></div>

        <!-- Store info -->
        <div
          class="admin-text admin-text-no-empty-paragraphs last-p-mb-0 bg-grey-lightest mb-4 p-3"
          v-html="storeModalStore.about"
        ></div>

        <!-- Actions -->
        <div class="d-flex">
          <a
            :href="storeModalStore.get_absolute_url"
            class="btn btn-primary btn-icon mt-1"
          >
            Shop {{ storeModalStore.store_name }}
            <IconChevronRight class="white ms-2" />
          </a>
          <!--
            Hide 'visit webshop' button, till we can add the right parameters
            to the final url -->
          <!--
            <a
            v-if="storeModalStore.store_url"
            :href="storeModalStore.store_url"
            target="_blank"
            class="btn btn-outline-secondary btn-icon mt-1 ms-3"
          >
            Visit webshop
            <IconExternalLink class="white ms-2" />
          </a> -->
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import SearchInput from './SearchInput.vue';
import Pagination from './Pagination.vue';
import Modal from './Modal.vue';
import FilterDropdown from './FilterDropdown.vue';
import IconChevronRight from '../../../../templates/icons/chevron-right.svg';
import IconChevronDown from '../../../../templates/icons/chevron-down.svg';
// import IconExternalLink from '../../../../templates/icons/external-link.svg';
import IconLoading from '../../../../templates/icons/loading.svg';

export default {
  components: {
    SearchInput,
    Pagination,
    Modal,
    FilterDropdown,
    IconChevronRight,
    IconChevronDown,
    // IconExternalLink,
    IconLoading,
  },
  props: {
    limit: {
      type: Number,
      default: 24,
    },
    viewURL: {
      type: String,
      default: '/',
    },
  },
  async serverPrefetch() {
    await this.$store.dispatch('countries/fetch');
    await this.$store.dispatch('viewset/fetch', {
      page: this.$route.query.page ? parseInt(this.$route.query.page) : 1,
      query: this.searchQuery,
      country: this.selectedCountry.id,
    });
  },
  data() {
    return {
      showStoreModal: false,
      storeModalStore: null,
      searchQuery: '',
      isLoading: false,
    };
  },
  computed: {
    currentPageNumber() {
      return parseInt(this.$store.state.viewset.query.page);
    },
    selectedCountry() {
      if (!this.$store.state.countries) return null;
      if (!this.$store.state.viewset.query.country) {
        return this.$store.state.countries.results.filter(
          c => c.country_code === this.$store.state.countryCode
        )[0];
      }
      return this.$store.state.countries.results.filter(
        c => c.id === this.$store.state.viewset.query.country
      )[0];
    },
    filteredAndOrderedCountries() {
      return this.$store.state.countries.results
        .filter(c => c.country_code !== 'WORLD')
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    },
  },
  watch: {
    async $route(to) {
      if (
        to.query.page &&
        parseInt(to.query.page) !==
          parseInt(this.$store.state.viewset.query.page)
      ) {
        this.scrollUpwards();
        await this.fetchStoreviewset({
          page: parseInt(to.query.page),
          query: this.searchQuery,
          country: this.selectedCountry.id,
        });
      } else if (
        !to.query.page &&
        parseInt(this.$store.state.viewset.query.page) > 1
      ) {
        this.scrollUpwards();
        await this.fetchStoreviewset({
          page: 1,
          query: this.searchQuery,
          country: this.selectedCountry.id,
        });
      }
    },
  },
  methods: {
    scrollUpwards() {
      this.$refs['search-input-component'].$el.scrollIntoView({
        behavior: 'instant',
      });
    },
    onCountrySelect(country) {
      document.activeElement.blur();
      this.fetchStoreviewset({
        page: 1,
        query: this.searchQuery,
        country: country.id,
      });
    },
    async fetchStoreviewset({ page, query, country }) {
      // If there is no page change and the search query is too short, don't
      // do anything
      if (
        page == this.currentPageNumber &&
        query === this.$store.state.viewset.query.query &&
        country == this.selectedCountry.id
      ) {
        return;
      }

      // Don't fetch when the search query is short (and there was no previous search query)
      if (query && query.length < 2 && !this.$store.state.viewset.query.query) {
        return;
      }

      this.isLoading = true;
      await this.$store.dispatch('viewset/fetch', {
        page,
        country,
        query: query.length > 1 ? query : '',
      });
      this.isLoading = false;

      // If the page number didn't change stop
      const queryPage = this.$route.query.page
        ? parseInt(this.$route.query.page)
        : 1;
      if (this.currentPageNumber === queryPage) {
        return;
      }

      // Otherwise update the url
      this.$router.push({
        query: {
          page: this.currentPageNumber > 1 ? this.currentPageNumber : undefined,
        },
      });
    },
  },
};
</script>
